import {
  BookingsQueryParams,
  getUrlQueryParamValue,
  mapBasketToCalendarSelections,
  storeCalendarSelections,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { navigateToCalendarPage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import { Basket } from '@wix/bookings-services-preferences-modal';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { CalendarState } from '../../controller';
import { CloseDialogAction } from '../closeDialog/closeDialog';

export type OnServicesPreferencesConfirm = (basket: Basket) => void;

export const createOnServicesPreferencesConfirm =
  (
    { context }: ActionFactoryParams<CalendarState, CalendarContext>,
    closeDialog: CloseDialogAction,
  ): OnServicesPreferencesConfirm =>
  async (basket) => {
    const {
      reloadWidget,
      isDateAndTimeViewMode,
      flowAPI: {
        controllerConfig: { wixCodeApi, platformAPIs },
        experiments,
      },
    } = context;

    const { session } = platformAPIs.storage;
    const isRemoveSkipPreferencesModalParamEnabled = experiments.enabled(
      'specs.bookings.removeSkipPreferencesModalParam',
    );

    const calendarSelections = mapBasketToCalendarSelections(basket);
    storeCalendarSelections({ calendarSelections, session });

    if (shouldPerformFullNavigation(basket, isDateAndTimeViewMode)) {
      const timezoneFromQueryParam = getUrlQueryParamValue(
        wixCodeApi,
        BookingsQueryParams.TIMEZONE,
      );
      const referralFromQueryParam = getUrlQueryParamValue(
        wixCodeApi,
        BookingsQueryParams.REFERRAL,
      );
      const resourceFromQueryParam = getUrlQueryParamValue(
        wixCodeApi,
        BookingsQueryParams.RESOURCE,
      );
      const locationFromQueryParam = getUrlQueryParamValue(
        wixCodeApi,
        BookingsQueryParams.LOCATION,
      );
      const bookingIdFromQueryParam = getUrlQueryParamValue(
        wixCodeApi,
        BookingsQueryParams.BOOKING_ID,
      );

      await navigateToCalendarPage(wixCodeApi, platformAPIs, {
        serviceSlugOrBasket: basket,
        navigationContext: {
          ...(!isRemoveSkipPreferencesModalParamEnabled && {
            skipPreferencesModal: true,
          }),
          ...(timezoneFromQueryParam && { timezone: timezoneFromQueryParam }),
          ...(referralFromQueryParam && { referral: referralFromQueryParam }),
          ...(resourceFromQueryParam && { resource: resourceFromQueryParam }),
          ...(locationFromQueryParam && { location: locationFromQueryParam }),
          ...(bookingIdFromQueryParam && {
            bookingId: bookingIdFromQueryParam,
          }),
        },
      });
    } else {
      if (!isRemoveSkipPreferencesModalParamEnabled) {
        wixCodeApi.location.queryParams.add({
          [BookingsQueryParams.SKIP_PREFERENCES_MODAL]: 'true',
        });
      }
      closeDialog();
      reloadWidget();
    }
  };

const shouldPerformFullNavigation = (
  basket: Basket,
  isDateAndTimeViewMode: boolean,
): boolean => {
  const hasMultipleItems = basket.selectedServicesPreferences.length > 1;
  const hasSingleItem = basket.selectedServicesPreferences.length === 1;
  const shouldNavigateFromSlugToDateAndTime =
    hasMultipleItems && !isDateAndTimeViewMode;
  const shouldNavigateFromDateAndTimeToSlug =
    hasSingleItem && isDateAndTimeViewMode;

  return (
    shouldNavigateFromSlugToDateAndTime || shouldNavigateFromDateAndTimeToSlug
  );
};
